import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HexAlphaColorPicker } from 'react-colorful';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import FilesService from '@services/FilesService';
import type { IBrandingProperty } from '@interfaces/map';
import type { IBrandingEntityValues } from '@interfaces/rooms';

import {
  BrandingValueErrorCode,
  buildBrandingEntityValues,
  getBrandingEntityCatalogue,
  normalizeBrandingColorValue,
  resolveBrandingPropertyWidget,
  toStoredBrandingValue,
  validateBrandingPropertyValue,
} from '../brandingEntities';
import { sendBrandingEntitiesUpdatedToUnreal } from '../brandingSpotsUnreal';
import { useEditorMode } from '../EditorModeContext';
import { useInspectorStyles } from './styles';

const AUTOSAVE_DEBOUNCE_MS = 600;

// Matches the QuickSwitcher's page: a map's configurations comfortably fit, and
// a configuration that falls outside the page reads as a failed load.
const BRANDINGS_PAGE_SIZE = 100;

const ALLOWED_TEXTURE_MIME_TYPES = ['image/png', 'image/jpeg'];

/**
 * Tells whether a picked file is a texture UE's image pipeline can load.
 *
 * @param file - File chosen in the row's file input.
 * @returns Whether the file may be uploaded.
 */
const isAllowedTextureFile = (file: File): boolean => {
  const mimeOk = !!file.type && ALLOWED_TEXTURE_MIME_TYPES.includes(file.type);
  const nameOk = /\.(png|jpe?g)$/i.test((file.name ?? '').trim());

  return mimeOk || nameOk;
};

const ERROR_MESSAGES: Record<
  BrandingValueErrorCode,
  { key: string; fallback: string }
> = {
  color: {
    key: 'editorMode.brandingEntityInvalidColor',
    fallback: 'Use a HEX color such as #RRGGBB or #RRGGBBAA.',
  },
  number: {
    key: 'editorMode.brandingEntityInvalidNumber',
    fallback: 'Enter a number, for example 1.5.',
  },
  integer: {
    key: 'editorMode.brandingEntityInvalidInteger',
    fallback:
      'Enter a whole number. Unreal refuses a fractional value here, so it is not rounded for you.',
  },
  boolean: {
    key: 'editorMode.brandingEntityInvalidBoolean',
    fallback: 'Use on or off.',
  },
  enum: {
    key: 'editorMode.brandingEntityInvalidEnum',
    fallback: 'Pick one of the values this build declared.',
  },
  assetPath: {
    key: 'editorMode.brandingEntityInvalidAssetPath',
    fallback: 'Enter a path starting with / or pick a declared value.',
  },
  textureUrl: {
    key: 'editorMode.brandingEntityInvalidTextureUrl',
    fallback: 'Enter an absolute http(s) URL.',
  },
};

interface IBrandingPropertyRowProps {
  property: IBrandingProperty;
  value: string;
  error: BrandingValueErrorCode | null;
  disabled: boolean;
  onChange: (property: IBrandingProperty, next: string) => void;
}

/**
 * Renders one catalogue property with the control its declared type implies.
 *
 * @param props.property - Catalogue property being edited.
 * @param props.value - Stored override; `''` means the property is not overridden.
 * @param props.error - Failing validation rule, or `null` when the value is fine.
 * @param props.disabled - Whether editing is blocked (loading, or no configuration).
 * @param props.onChange - Called with the raw value on every edit (debounced upstream).
 */
const BrandingPropertyRow = ({
  property,
  value,
  error,
  disabled,
  onChange,
}: IBrandingPropertyRowProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const inputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const widget = resolveBrandingPropertyWidget(property);
  const { propertyId } = property;
  const isOverridden = value !== '';
  const allowedValues = property.allowedValues ?? [];

  const stopKeyPropagation = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  const renderTextInput = (
    testId: string,
    placeholder?: string,
    inputMode?: 'decimal' | 'numeric' | 'url' | 'text',
  ) => (
    <TextField
      inputRef={inputRef}
      fullWidth
      size="small"
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => onChange(property, event.target.value)}
      onKeyDown={stopKeyPropagation}
      onKeyUp={stopKeyPropagation}
      sx={styles.textField}
      inputProps={{
        'data-testid': testId,
        ...(inputMode ? { inputMode } : {}),
      }}
    />
  );

  /**
   * Uploads the picked texture and stores the URL it lands on.
   *
   * The value goes through `onChange`, so the upload is saved by the same
   * debounced autosave as a typed URL. The backend moves the file out of the
   * temporary `uploads/` prefix when it persists the value.
   */
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change event.
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isAllowedTextureFile(file)) {
      setUploadError(
        translate(
          'editorMode.brandingEntityUploadUnsupported',
          'Use a PNG or JPEG image.',
        ),
      );
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const [uploadInfo] = await FilesService.uploadFilesWithProgress([file]);

      onChange(property, uploadInfo.url);
    } catch (uploadFailure) {
      console.error(
        `Failed to upload branding texture: ${
          (uploadFailure as Error)?.message
        }`,
        { propertyId },
      );
      setUploadError(
        translate(
          'editorMode.brandingEntityUploadFailed',
          'Upload failed. Try again.',
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const renderControl = () => {
    if (widget === 'unsupported') {
      return (
        <Typography sx={styles.emptyHint}>
          {translate(
            'editorMode.brandingEntityUnsupportedType',
            'This version of the hub cannot edit this value type yet.',
          )}
        </Typography>
      );
    }

    if (widget === 'colorPicker') {
      const swatchColor = normalizeBrandingColorValue(value) ?? 'transparent';

      return (
        <>
          <Box sx={styles.colorRow}>
            <Box
              sx={[
                styles.colorSwatch,
                { backgroundColor: swatchColor },
                !isOverridden && styles.spotThumbPlaceholder,
              ]}
              onClick={(event) => {
                if (!disabled) {
                  setColorAnchorEl(event.currentTarget);
                }
              }}
              data-testid={`branding-entity-${propertyId}-color-swatch`}
            />
            <Box sx={styles.colorRowText}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                value={value}
                disabled={disabled}
                placeholder={property.default ?? '#RRGGBBAA'}
                onChange={(event) => onChange(property, event.target.value)}
                onKeyDown={stopKeyPropagation}
                onKeyUp={stopKeyPropagation}
                sx={styles.pickerHexField}
                inputProps={{
                  maxLength: 9,
                  'data-testid': `branding-entity-${propertyId}-color-input`,
                }}
              />
            </Box>
          </Box>
          <Popover
            open={Boolean(colorAnchorEl)}
            anchorEl={colorAnchorEl}
            onClose={() => setColorAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: styles.pickerPopover }}
          >
            <HexAlphaColorPicker
              color={normalizeBrandingColorValue(value) ?? '#ffffffff'}
              onChange={(next) => onChange(property, next)}
            />
          </Popover>
        </>
      );
    }

    if (widget === 'toggle') {
      // A switch has no "not overridden" position, so it shows the value that
      // is actually in effect — the override when there is one, the level
      // default otherwise — and the reset button removes the override.
      const effective = isOverridden ? value : property.default ?? 'false';

      return (
        <Switch
          size="small"
          inputRef={inputRef}
          checked={effective === 'true'}
          disabled={disabled}
          onKeyDown={stopKeyPropagation}
          onKeyUp={stopKeyPropagation}
          onChange={(event) =>
            onChange(property, event.target.checked ? 'true' : 'false')
          }
          inputProps={
            {
              'data-testid': `branding-entity-${propertyId}-toggle`,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
        />
      );
    }

    if (widget === 'select') {
      if (allowedValues.length === 0) {
        return (
          <Typography sx={styles.emptyHint}>
            {translate(
              'editorMode.brandingEntityNoAllowedValues',
              'This build declared no values for this property.',
            )}
          </Typography>
        );
      }

      return (
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={allowedValues.includes(value) ? value : ''}
          disabled={disabled}
          onKeyDown={stopKeyPropagation}
          onKeyUp={stopKeyPropagation}
          onChange={(event) => onChange(property, event.target.value)}
          sx={styles.textField}
          SelectDisplayProps={
            {
              'data-testid': `branding-entity-${propertyId}-select`,
            } as React.HTMLAttributes<HTMLDivElement>
          }
          MenuProps={{
            MenuListProps: {
              'data-testid': `branding-entity-${propertyId}-select-list`,
            } as React.HTMLAttributes<HTMLUListElement>,
          }}
        >
          <MenuItem
            value=""
            data-testid={`branding-entity-${propertyId}-option-authored`}
          >
            {translate(
              'editorMode.brandingEntityUseAuthored',
              'Use the value from the level',
            )}
          </MenuItem>
          {allowedValues.map((allowed) => (
            <MenuItem
              key={allowed}
              value={allowed}
              data-testid={`branding-entity-${propertyId}-option-${allowed}`}
            >
              {allowed}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (widget === 'numberField') {
      return renderTextInput(
        `branding-entity-${propertyId}-number-input`,
        property.default,
        'decimal',
      );
    }

    if (widget === 'integerField') {
      return renderTextInput(
        `branding-entity-${propertyId}-number-input`,
        property.default,
        'numeric',
      );
    }

    if (widget === 'urlField') {
      return (
        <Box sx={styles.brandingPropertyUploadRow}>
          {renderTextInput(
            `branding-entity-${propertyId}-url-input`,
            property.default ?? 'https://',
            'url',
          )}
          <Tooltip
            title={translate(
              'editorMode.brandingEntityUpload',
              'Upload a PNG or JPEG',
            )}
            placement="bottom"
            enterDelay={400}
          >
            <IconButton
              size="small"
              component="label"
              disabled={disabled || isUploading}
              sx={styles.brandingPropertyResetButton}
              aria-label={translate(
                'editorMode.brandingEntityUpload',
                'Upload a PNG or JPEG',
              )}
              data-testid={`branding-entity-${propertyId}-upload-btn`}
            >
              <UploadFileOutlinedIcon sx={{ fontSize: 15 }} />
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleFileSelected}
                data-testid={`branding-entity-${propertyId}-upload-input`}
              />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (widget === 'pathField') {
      return renderTextInput(
        `branding-entity-${propertyId}-path-input`,
        property.default ?? '/Game/…',
        'text',
      );
    }

    return renderTextInput(
      `branding-entity-${propertyId}-text-input`,
      property.default,
      'text',
    );
  };

  return (
    <Box
      sx={styles.brandingPropertyRow}
      data-testid={`branding-entity-property-row-${propertyId}`}
    >
      <Box sx={styles.brandingPropertyHeader}>
        <Tooltip title={propertyId} placement="bottom" enterDelay={400}>
          <Typography sx={styles.brandingPropertyLabel}>
            {property.displayName || propertyId}
          </Typography>
        </Tooltip>
        <Box component="span" sx={styles.brandingPropertyTypeChip}>
          {property.type}
        </Box>
        {isOverridden && widget !== 'unsupported' && (
          <Tooltip
            title={translate(
              'editorMode.brandingEntityReset',
              'Restore the value from the level',
            )}
            placement="bottom"
            enterDelay={400}
          >
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => onChange(property, '')}
              sx={styles.brandingPropertyResetButton}
              aria-label={translate(
                'editorMode.brandingEntityReset',
                'Restore the value from the level',
              )}
              data-testid={`branding-entity-${propertyId}-reset-btn`}
            >
              <RestartAltOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {renderControl()}

      <Typography sx={styles.brandingPropertyHint}>
        {property.default === undefined
          ? translate('editorMode.brandingEntityNoDefault', 'No level default')
          : translate(
              'editorMode.brandingEntityDefault',
              'Level default: {{value}}',
              {
                value: property.default,
              },
            )}
      </Typography>
      {property.authoredAssetPath && (
        <Typography sx={styles.brandingPropertyHint}>
          {translate(
            'editorMode.brandingEntityAuthoredAsset',
            'Authored asset: {{value}}',
            { value: property.authoredAssetPath },
          )}
        </Typography>
      )}
      {isUploading && (
        <Typography sx={styles.brandingPropertyHint}>
          {translate('editorMode.brandingEntityUploading', 'Uploading…')}
        </Typography>
      )}
      {uploadError && (
        <Typography sx={styles.helperError}>{uploadError}</Typography>
      )}
      {error && (
        <Typography sx={styles.helperError}>
          {translate(ERROR_MESSAGES[error].key, ERROR_MESSAGES[error].fallback)}
        </Typography>
      )}
    </Box>
  );
};

interface IBrandingEntityInspectorProps {
  entityId: string;
}

/**
 * Renders the editor for one brandable entity the current build declares.
 *
 * Saves are debounced and always carry the entity's complete property set, so
 * the backend's per-entity replace never drops a property the user did not
 * touch; the same set is pushed to Unreal once per save.
 *
 * @param props.entityId - Catalogue entity being edited.
 */
const BrandingEntityInspector = ({
  entityId,
}: IBrandingEntityInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { setAutosaveStatus } = useEditorMode();
  const { sendMessageToUnreal } = useMytaverse();
  const { currentRoom, currentBrandingId, isBrandingModeEnabled } =
    useMytaverseEvent();

  const mapId = currentRoom?.map?.mapId;
  const isEditingLive =
    !!currentBrandingId && currentBrandingId === currentRoom?.activeBrandingId;

  const entity = useMemo(
    () =>
      getBrandingEntityCatalogue(currentRoom?.map).find(
        (candidate) => candidate.entityId === entityId,
      ) ?? null,
    [currentRoom?.map, entityId],
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<
    Record<string, BrandingValueErrorCode | null>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadedRef = useRef(false);
  // The save the debounce is holding. Kept in a ref so it can still be flushed
  // from a cleanup, which runs with a stale closure over state.
  const pendingSaveRef = useRef<{
    brandingId: string;
    values: Record<string, string>;
  } | null>(null);
  // Properties edited in this session; only these are held against a save, so a
  // stored value this version considers invalid cannot lock the panel.
  const touchedRef = useRef<Set<string>>(new Set());

  const persist = useCallback(
    async (brandingId: string, nextValues: Record<string, string>) => {
      const propertiesById = new Map(
        (entity?.properties ?? []).map((property) => [
          property.propertyId,
          property,
        ]),
      );

      const stored: Record<string, string> = {};
      Object.entries(nextValues).forEach(([propertyId, raw]) => {
        const property = propertiesById.get(propertyId);
        stored[propertyId] = property
          ? toStoredBrandingValue(property, raw)
          : raw.trim();
      });

      const brandingEntities: IBrandingEntityValues = buildBrandingEntityValues(
        entityId,
        stored,
      );

      setAutosaveStatus('saving');
      try {
        const updated = await BrandingsService.updateBranding(brandingId, {
          brandingEntities,
        });

        // Unreal is told what was stored, not what was sent: an uploaded
        // texture is relocated while it is persisted, and its temporary URL
        // stops resolving the moment that happens.
        const persistedValues = updated?.brandingEntities?.[entityId];
        const appliedEntities: IBrandingEntityValues = persistedValues
          ? { [entityId]: persistedValues }
          : brandingEntities;

        if (isBrandingModeEnabled) {
          sendBrandingEntitiesUpdatedToUnreal(
            sendMessageToUnreal,
            appliedEntities,
          );
        }

        // Adopt the stored values, unless the user has typed since — then their
        // edit is newer and the next save will reconcile it.
        if (persistedValues && !debounceRef.current) {
          setValues({ ...persistedValues });
        }

        setAutosaveStatus('saved');
      } catch (saveError) {
        console.error(
          `Failed to save branding entity values: ${
            (saveError as Error)?.message
          }`,
          { brandingId, entityId },
        );
        setAutosaveStatus('error');
      }
    },
    [
      entity?.properties,
      entityId,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
    ],
  );

  /**
   * Sends the save the debounce is still holding, if any.
   *
   * Called before the panel stops watching a configuration — on unmount and on a
   * configuration switch — so an edit made in the last 600 ms is not lost, and a
   * timer belonging to the previous configuration cannot fire after the switch.
   */
  const flushPendingSave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;

    if (pending) {
      persist(pending.brandingId, pending.values);
    }
  }, [persist]);

  useEffect(() => {
    if (!currentBrandingId || !mapId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setHasLoadFailed(false);
    initialLoadedRef.current = false;
    touchedRef.current = new Set();

    const load = async () => {
      try {
        const response = await BrandingsService.getBrandings({
          mapId,
          limit: BRANDINGS_PAGE_SIZE,
        });
        if (cancelled) {
          return;
        }

        const branding = response.brandings?.find(
          (candidate) => candidate?.brandingId === currentBrandingId,
        );

        // Editing without the stored values would replace the whole entity with
        // whatever single property gets touched, so a configuration that did not
        // come back is treated as a failed load rather than as "no overrides".
        if (!branding) {
          throw new Error(
            `Configuration ${currentBrandingId} not found for map ${mapId}`,
          );
        }

        setValues({ ...(branding.brandingEntities?.[entityId] ?? {}) });
        setErrors({});
        initialLoadedRef.current = true;
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        console.error(
          `Failed to load branding entity values: ${
            (loadError as Error)?.message
          }`,
          { brandingId: currentBrandingId, entityId },
        );
        setHasLoadFailed(true);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      // The values being replaced belong to the configuration being left.
      flushPendingSave();
    };
  }, [currentBrandingId, mapId, entityId, flushPendingSave]);

  const handleChange = useCallback(
    (property: IBrandingProperty, next: string) => {
      const nextValues = { ...values, [property.propertyId]: next };
      setValues(nextValues);
      touchedRef.current.add(property.propertyId);

      const nextErrors: Record<string, BrandingValueErrorCode | null> = {};
      for (const candidate of entity?.properties ?? []) {
        if (!touchedRef.current.has(candidate.propertyId)) {
          continue;
        }

        nextErrors[candidate.propertyId] = validateBrandingPropertyValue(
          candidate,
          nextValues[candidate.propertyId] ?? '',
        );
      }
      setErrors(nextErrors);

      if (!initialLoadedRef.current || !currentBrandingId) {
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // A save carries the entity's whole set, so one invalid value would ride
      // along with the others and Unreal would refuse it. Nothing is sent while
      // any edited property is invalid; the rows say which.
      if (Object.values(nextErrors).some((errorCode) => !!errorCode)) {
        pendingSaveRef.current = null;
        return;
      }

      pendingSaveRef.current = {
        brandingId: currentBrandingId,
        values: nextValues,
      };

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        flushPendingSave();
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [currentBrandingId, entity?.properties, flushPendingSave, values],
  );

  if (!entity) {
    return (
      <Typography sx={styles.emptyHint}>
        {translate(
          'editorMode.brandingEntityMissing',
          'This object is no longer declared by the active build.',
        )}
      </Typography>
    );
  }

  const disabled = isLoading || hasLoadFailed || !currentBrandingId;

  return (
    <Box>
      {isEditingLive && (
        <Box sx={styles.liveWarning}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: '#FFB800' }} />
          <Typography sx={styles.liveWarningText}>
            {translate('editorMode.liveBrandingWarning')}
          </Typography>
        </Box>
      )}

      <Box sx={styles.section}>
        <Box sx={styles.brandingEntityMeta}>
          <Typography sx={styles.brandingEntityMetaText}>
            {translate('editorMode.brandingEntityType', 'Kind: {{value}}', {
              value: entity.entityType,
            })}
          </Typography>
          <Typography sx={styles.brandingEntityMetaText}>
            {translate(
              'editorMode.brandingEntityMaps',
              'Visible on: {{value}}',
              { value: entity.maps.join(', ') },
            )}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionLabel}>
          {translate('editorMode.brandingEntityProperties', 'Properties')}
        </Typography>
        {hasLoadFailed && (
          <Typography sx={styles.helperError}>
            {translate(
              'editorMode.brandingEntityLoadFailed',
              'Could not load the saved values. Editing is disabled so nothing is overwritten — reopen the object to retry.',
            )}
          </Typography>
        )}
        {entity.properties.length === 0 ? (
          <Typography sx={styles.emptyHint}>
            {translate(
              'editorMode.brandingEntityNoProperties',
              'This object declares nothing to edit.',
            )}
          </Typography>
        ) : (
          entity.properties.map((property) => (
            <BrandingPropertyRow
              key={property.propertyId}
              property={property}
              value={values[property.propertyId] ?? ''}
              error={errors[property.propertyId] ?? null}
              disabled={disabled}
              onChange={handleChange}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default BrandingEntityInspector;
